import React from 'react'
import AirconRequestSection from '../index/service-category/aircon-service/AirconRequestSection'
import { t } from '../../utils/markets'
import { currencyLabel, formatAmount, formatMoney } from '../../utils/format-money'
import { heroGroups } from '../../utils/market-aircon-booking'
import { useMarketBooking } from './MarketBooking'

// Which AirconScope ("Service Details") entry each hero tab opens.
const scopeKeyFor = (panelType, tab) => {
    if (panelType === 'repair') return 'repair'
    if (panelType === 'annual' || panelType === 'drainFlush') return 'general'
    return tab
}

// Warranty days for a hero tab (utils/faq.js states the same for Singapore).
const warrantyDays = (panelType, tab) => {
    // Annual plans are general servicing (their tabs reuse the theme keys).
    if (panelType === 'annual' || panelType === 'repair') return 30
    if (panelType === 'gas' || tab === 'gasRefill' || tab === 'gasRefillHalf') return 90
    if (panelType === 'drainFlush' || tab === 'chemicalWash' || tab === 'chemicalOverhaul') return 60
    return 30
}

/**
 * UAE / Malaysia aircon hero: the Singapore AirconRequestSection and
 * AirconBooking card, fed the market's tabs and price tiles. Booking state,
 * sheets and the sticky bar come from the surrounding <MarketBooking>.
 *
 * Differences from Singapore, all deliberate:
 *  - prices and labels come from the market (AED / RM, "AC" / "aircond");
 *  - no Google rating (unless the market's googleRating switch is on), no
 *    "20% off", warranty or UEN claims - those describe the Singapore business.
 */
export const MarketAirconHero = ({ market, tables, panel = {}, title, subtitle, image, imageAlt, pills, showRating = false }) => {
    const { book, openItemSelection, showJobScope, setIsQuickBook, setIsCustomBook } = useMarketBooking()
    const groups = heroGroups(market, tables, panel)
    const cd = market.countryCd
    const unitType = panel.defaultMount === 'Wall' || cd !== 'ae' ? t(market, 'mountWall') : t(market, 'mountDucted')

    const config = {
        tabs: groups.map(({ value, label, ariaLabel }) => ({ value, label, ariaLabel })),
        tilesFor: (tab) => (groups.find((g) => g.value === tab) ?? groups[0]).tiles.map((tile) => ({
            key: tile.key,
            label: tile.label,
            // Same label on phones as on desktop ("2 AC units"), as on the SG card.
            shortLabel: tile.label,
            currencyCd: market.currency,
            price: tile.price,
            priceLabel: formatMoney(tile.price, cd),
            priceParts: { currency: currencyLabel(cd), amount: formatAmount(tile.price, cd) },
            ariaLabel: `Book ${tile.summary} for ${formatMoney(tile.price, cd)}`,
            onBook: () => book(tile),
        })),
        onMore: openItemSelection,
        onShowDetails: (tab) => showJobScope(scopeKeyFor(panel.type, tab), unitType),
        moreAriaLabel: `Show more ${t(market, 'acShort')} service options`,
        pills,
        showRating,
        // Same warranty as Singapore: 30 days general / repair, 60 days chemical
        // cleaning and drainage, 90 days gas.
        highlights: (tab) => [`${warrantyDays(panel.type, tab)} days warranty`, 'Travel included'],
        discountText: 'New-customer discount on your first booking',
        showCompanyBadge: false,
        guideMarket: market,
    }

    return (
        <AirconRequestSection
            market={config}
            sectionId="booking"
            heroImage={image}
            mobileImageAlt={imageAlt}
            title={title}
            subtitle={subtitle}
            countryName={market.countryName}
            requestType="Aircon Services"
            // Stable useState setters: AirconBooking's mount reset lists them
            // as effect dependencies.
            setIsQuickBook={setIsQuickBook}
            setIsCustomBook={setIsCustomBook}
        />
    )
}
